import * as React from "react";
import { Form, useFormApi } from "informedv4";
import DocumentTitle from "../../misc/components/DocumentTitle";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link, useParams } from "react-router-dom";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import { StudyForm, ExtractBase64Image, StudyFormComponent } from "./StudyForm";
import { StudyOverreadForm } from "./StudyOverreadForm";
import { StudyMetricsForm } from "./StudyMetricsForm";
import { Country, DeviceType } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { DeviceTypes } from "../../misc/api/apiTypes";

export const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
    return (
        <div className="h2 pull-right">
            <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                Save
            </button>&nbsp;
            <Link className="btn btn-primary btn-outline" to="/studies" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                Return to List
            </Link>
        </div>
    );
}

export interface EditStudyFormProps {
    study: StudyForm;
}

export const EditStudyForm = ({ study }: EditStudyFormProps) => {
    const formApi = useFormApi();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const [selectedDeviceTypes, setSelectedDeviceTypes] = React.useState<DeviceType[]>(study.DeviceTypes);
    const [logoToUpload, setLogoToUpload] = React.useState<File>();
    const legacyClient = useWebPortalLegacyClient();

    const client = useWebPortalApiClient();

    const onPreAuditSubmit = async () => {
        setErrorAlert(null);
        setSubmitting(true);
        setSuccessAlert(false);
        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSubmitting(false);
            setSuccessAlert(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            setAuditModalVisible(false);
            return setErrorAlert("Errors exist on the page");
        }

        const apiForm = {
            ...state.values,
        }

        let base64StringLogo;
        if (logoToUpload) {
            base64StringLogo = await ExtractBase64Image(logoToUpload);
        }

        try {
            const studyResult = await client.client.patchStudy(
                {
                    name: apiForm.Name as string,
                    protocol: state.values.Protocol as string,
                    sessionTypeIds: (state.values.SessionTypes != undefined ? (state.values.SessionTypes as any).map(st => st.id) : []),
                    countriesIds: state.values.Countries != undefined ? (state.values.Countries as any).map(c => c.id) : [],
                    languageIds: state.values.DiaryLanguage != undefined ? (state.values.DiaryLanguage as any).map(l => l.localeName) : [],
                    deviceTypeIds: state.values.DeviceTypes != undefined ? (state.values.DeviceTypes as any).map(dt => dt.id) : [],
                    description: state.values.Description as string ?? null,
                    targetSubjects: state.values.TargetSubjects as number ?? null,
                    targetSessions: state.values.TargetSessions as number  ?? null,
                    logo: base64StringLogo,
                    logoMediaType: logoToUpload?.type,
                    welcomeMessage: state.values.WelcomeMessage as string ?? null,
                    vitalographContactEmail: state.values.StudyContactEmail as string ?? null,
                    diaryConfiguration: state.values.DiaryConfiguration as string
                }, study.Id, auditForm.Annotation);


            await legacyClient.patchSetting("reporting.rootFolder", state.values.RootReportingFolder, study.Id);

            if (studyResult) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setErrorAlert(ex.message !== undefined ? ex.message : ex.Message);
        }

        setAuditModalVisible(false);
        setSubmitting(false);
        window.scrollTo(0, 0);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Edit Study</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Core Details</h4>
                        </div>
                        <div className="panel-body">
                            <StudyFormComponent onAlert={(alert) => setErrorAlert(alert)} onFileAttached={(file) => setLogoToUpload(file)} onDeviceTypeChanged={setSelectedDeviceTypes} />
                        </div>
                    </div>
                    <div id="overreadSetupPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Over-Read Setup</h4>
                        </div>
                        <div className="panel-body">
                            <StudyOverreadForm />
                        </div>
                    </div>
                    <div id="metricsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Metrics</h4>
                        </div>
                        <div className="panel-body">
                            <StudyMetricsForm />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onPreAuditSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={async (audit) => await onSubmit(audit)}
                />
            ) : null}
        </>
    );
};

export const EditStudyScreen = () => {
    const { id } = useParams<{ id: string }>();
    const { currentUserPermissions } = useSecurityContext();
    const client = useWebPortalApiClient();
    const [studyToEdit, setStudyToEdit] = React.useState<StudyForm>();
    const legacyClient = useWebPortalLegacyClient();


    React.useEffect(() => {
        const getData = async () => {
            const studyResult = await client.client.getStudy(id, undefined);
            const sessionTypes = await client.client.getStudySessionTypes(undefined, `studyId eq ${id}`);
            const deviceTypes = await (await client.client.getDeviceTypes()).value.filter(dt => studyResult.deviceTypeIds.some(id => dt.id === id));
            const diaryLanguages = await client.client.getStudyLanguages(undefined, `studyId eq ${id}`);
            const studyCountries = await client.client.getStudyCountries(undefined, `studyId eq ${id}`);
            const studyRootFolder = await legacyClient.getSetting("reporting.rootFolder", id);
            const countries: Country[] = [];

            for (const studyCountry of studyCountries.value) {
                countries.push({
                    id: studyCountry.countryCode,
                    localPostalAddress: studyCountry.localPostalAddress,
                    name: studyCountry.name,
                    tollFreeNumber: studyCountry.tollFreeNumber,
                })
            }

            const formStudy: StudyForm = {
                Id: studyResult.id,
                Name: studyResult.name,
                Protocol: studyResult.protocol,
                SessionTypes: sessionTypes.value,
                Description: studyResult.description,
                DeviceTypes: deviceTypes,
                DiaryLanguage: diaryLanguages.value,
                Countries: countries,
                Logo: studyResult.logo,
                WelcomeMessage: studyResult.welcomeMessage,
                TargetSubjects: studyResult.targetSubjects,
                TargetSessions: studyResult.targetSessions,
                StudyContactEmail: studyResult.vitalographContactEmail,
                DiaryConfiguration : studyResult.diaryConfiguration,
                RootReportingFolder: studyRootFolder
            }

            setStudyToEdit(formStudy);
        }

        getData();
    }, [id]);

    if (!studyToEdit) return (<LoadingWithMessage message="Loading Study..." />);

    return (
        <Authorization studyId={id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.EditStudy]}>
            <DocumentTitle title="Edit Study | Vitalograph Web Portal">
                <Form validateOn="submit" noValidate initialValues={JSON.parse(JSON.stringify(studyToEdit))}>
                    <EditStudyForm study={studyToEdit} />
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
